import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { BsTextLeft } from 'react-icons/bs';
import { FaEdit, FaInstagram, FaMapPin } from 'react-icons/fa';
import { FiPhone } from 'react-icons/fi';
import { MdPhoneAndroid } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { OptionsOrGroups, GroupBase, MultiValue } from 'react-select';
import * as Yup from 'yup';

import Button from 'components/Button';
import CustomSelect from 'components/CustomSelect';
import DynamicInput from 'components/DynamicInput';
import DynamicTextArea from 'components/DynamicTextArea';
import LoadingContainer from 'components/LoadingContainer';
import Category from 'interfaces/Category';
import Service from 'interfaces/Service';
import { db } from 'services/firebase';
import getValidationErrors from 'utils/getValidationErrors';

import {
  Container,
  ServiceForm,
  ServiceFormFields,
  ServiceFormSection,
} from './styles';

interface ServiceFormData {
  name: string;
  cellphone: string;
  phone: string;
  instagram: string;
  description: string;
  location: string;
}

const EditService: React.FC = () => {
  const history = useHistory();

  const formRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState(false);
  const [serviceId, setServiceId] = useState('');
  const [service, setService] = useState<Service>();
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [options, setOptions] = useState<
    OptionsOrGroups<Category, GroupBase<Category>> | undefined
  >([]);

  const updateCategory = (newOptions: MultiValue<Category>): void => {
    setSelectedCategories(newOptions as Category[]);
  };

  const handleSubmit = useCallback(
    async (data: ServiceFormData) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape(
          {
            name: Yup.string().required('Nome obrigatório'),
            cellphone: Yup.string().when('phone', {
              is: (phone: string) => !phone || phone.length === 0,
              then: Yup.string().required(
                'Informe pelo menos um celular ou um telefone fixo',
              ),
            }),
            phone: Yup.string().when('cellphone', {
              is: (cellphone: string) => !cellphone || cellphone.length === 0,
              then: Yup.string().required(
                'Informe pelo menos um celular ou um telefone fixo',
              ),
            }),
          },
          [['cellphone', 'phone']],
        );

        await schema.validate(data, {
          abortEarly: false,
        });

        if (selectedCategories.length === 0) {
          toast.error('Você precisa selecionar pelo menos uma categoria');
          return;
        }

        if (selectedCategories.length > 3) {
          toast.error('Você pode selecionar no máximo 3 categorias');
          return;
        }

        await updateDoc(doc(db, 'services', serviceId), {
          name: data.name,
          description: data.description,
          cellphones: data.cellphone === '' ? [] : [data.cellphone],
          categories: selectedCategories,
          instagram: data.instagram,
          location: data.location,
          phones: data.phone === '' ? [] : [data.phone],
          dateModified: Timestamp.now(),
        });

        toast.success('Serviço editado com sucesso!');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          toast.error('Há erros de validação. Corrija-os abaixo', {
            duration: 2000,
          });

          formRef.current?.setErrors(errors);
          return;
        }

        toast.error(`Erro ao editar serviço. Erro: ${(err as Error).message}`, {
          duration: 2000,
        });
      }
    },
    [selectedCategories, serviceId],
  );

  useEffect(() => {
    const loadCategories = async (): Promise<void> => {
      const q = query(collection(db, 'categories'));

      const snapshot = await getDocs(q);

      setOptions(snapshot.docs.map((document) => document.data() as Category));
    };

    loadCategories();
  }, []);

  useEffect(() => {
    const loadServiceData = async (): Promise<void> => {
      setLoading(true);

      const currentUrl = window.location.href.slice(0, -5);
      const id = currentUrl.substring(currentUrl.lastIndexOf('/') + 1);

      setServiceId(id);

      const serviceDoc = await getDoc(doc(db, 'services', id));

      const serviceData = serviceDoc.data() as Service;

      if (!serviceData) {
        toast.error(
          'Erro ao carregar informações do serviço. Recarregue a página e tente novamente',
          { duration: 2000 },
        );
        history.push('/');
        return;
      }

      setService(serviceData);
      setSelectedCategories(serviceData.categories);
      setLoading(false);
    };

    loadServiceData();
  }, [history]);

  return (
    <Container>
      {loading || !service ? (
        <LoadingContainer />
      ) : (
        <ServiceForm ref={formRef} onSubmit={handleSubmit}>
          <h2>Editar serviço</h2>
          <ServiceFormFields>
            <ServiceFormSection>
              <h3>Informações gerais</h3>
              <DynamicInput
                name="name"
                defaultValue={service.name}
                icon={FaEdit}
              />
              <CustomSelect
                options={options}
                defaultValue={selectedCategories}
                onChange={updateCategory}
                getOptionValue={(category) => category.id}
                formatOptionLabel={(category) => category.readableName}
                isSearchable
                isMulti
                placeholder="Selecionar Categoria"
              />
              <DynamicTextArea
                name="description"
                placeholder="Descrição breve do serviço"
                defaultValue={service.description}
                icon={BsTextLeft}
              />
              <DynamicInput
                name="location"
                placeholder="Endereço"
                defaultValue={service.location}
                icon={FaMapPin}
              />
            </ServiceFormSection>

            <ServiceFormSection>
              <h3>Contatos</h3>
              <DynamicInput
                name="cellphone"
                placeholder="Celular"
                defaultValue={service.cellphones[0]}
                icon={MdPhoneAndroid}
              />
              <DynamicInput
                name="phone"
                placeholder="Telefone"
                defaultValue={service.phones[0]}
                icon={FiPhone}
              />
              <DynamicInput
                name="instagram"
                placeholder="Usuário no Instagram"
                defaultValue={service.instagram}
                icon={FaInstagram}
              />
            </ServiceFormSection>
          </ServiceFormFields>

          <Button type="submit" style={{ width: '250px' }}>
            Salvar
          </Button>
        </ServiceForm>
      )}
    </Container>
  );
};

export default EditService;
